import { Chance } from 'chance';
import isObject from 'lodash/isObject';
import {
  PapiDeviation,
  PapiDeviationBackground,
  PapiEditorText,
  PapiExtendedDeviation,
  PapiUser,
} from '@wix/da-papi-types';

import { deviationsByType } from '../deviations';
import { pickone, word, url, n, bool, integer, paragraph } from '../helpers';
import { users } from '../users';
import { groups } from '../groups';
import { coreBadgesLimited } from '../badges';
import { getDeviationBackground } from './deviation';
import { generateAdditionalMedia } from './multiImageDeviation';
import { makeEditorTextWithOptions } from './editorText';
import { makeRelatedContent } from './relatedContent';
import { makeAdoptableOffer } from './adoptables';
import { makeDiscount } from './discount';

const chance = new Chance();

function makeTextContent(textContent?: PapiEditorText) {
  if (!textContent) {
    return;
  }

  if (textContent.html.markup) {
    return textContent;
  }

  switch (textContent.html.type) {
    case 'draft':
      return makeEditorTextWithOptions({
        legacy: false,
        excerpt: textContent.excerpt,
        sentences: 10,
      });
    case 'writer':
    default:
      return makeEditorTextWithOptions({
        legacy: true,
        excerpt: textContent.excerpt,
        sentences: 10,
      });
  }
}

export interface AdoptableConfig {
  currentUserRelation: 'owner' | 'creator' | 'creator-and-owner' | 'other';
  canCurrentUserBuy: boolean;
  isResaleAllowed?: boolean;
  saleCount?: number;
  listingStatus?:
    | 'sold_via_offer'
    | 'sold_via_buy_it_now'
    | 'not_onsale'
    | {
        buyItNow: boolean;
        offerStatus:
          | 'offers-not-accepted'
          | 'countdown-running-no-offers'
          | 'countdown-running-some-offers'
          | 'expired-no-offers'
          | 'expired-some-offers'
          | 'pending-no-offers'
          | 'pending-some-offers'
          | 'before-offer-window';
      };
  withOffersList?: boolean;
  isGiftable?: boolean;
  isFreeGiftable?: boolean;
  freeGiftsRemaining?: number;
  wasGifted?: boolean;
  earlyAccess?: any;
}

function makeAdoptableData(
  deviation: PapiDeviation,
  adoptableConfig: AdoptableConfig,
  authorOverride?: PapiUser
) {
  const {
    isResaleAllowed,
    canCurrentUserBuy,
    currentUserRelation,
    saleCount,
    listingStatus = {
      buyItNow: true,
      offerStatus: 'countdown-running-no-offers',
    },
    isGiftable,
    wasGifted,
    earlyAccess,
  } = adoptableConfig;

  let baseData: Partial<PapiDeviation['adoptable']> = {};
  let extendedData: Partial<PapiDeviation['extended']['adoptable']> = {};

  const nowTs = new Date().getTime();
  const oneDayAheadTs = new Date(nowTs + 24 * 60 * 60 * 1000).toISOString();
  const oneDayAgoTs = new Date(nowTs - 24 * 60 * 60 * 1000).toISOString();
  const offerEndTime = oneDayAheadTs;

  const offerList = [
    makeAdoptableOffer(),
    makeAdoptableOffer(),
    makeAdoptableOffer(),
    makeAdoptableOffer(),
  ];
  if (isObject(listingStatus)) {
    switch (listingStatus.offerStatus) {
      case 'countdown-running-no-offers':
        baseData = {
          status: 'onsale',
          thumbSaleState: 'open',
          offers: {
            offerPrice: 10,
            numOffers: 0,
            offerEndTime,
          },
        };
        extendedData = {
          offers: { ...baseData.offers },
        };
        break;
      case 'countdown-running-some-offers':
        baseData = {
          status: 'onsale',
          thumbSaleState: 'open',
          offers: {
            offerPrice: 10,
            numOffers: offerList.length,
            offerEndTime,
          },
        };
        extendedData = {
          offers: { ...baseData.offers, highestOffer: 15, offerList },
        };
        break;
      case 'expired-no-offers':
        baseData = {
          status: 'not_onsale',
          thumbSaleState: 'expired',
        };
        extendedData = {
          offers: { ...baseData.offers },
        };
        break;
      case 'expired-some-offers':
        baseData = {
          status: 'not_onsale',
          thumbSaleState: 'expired',
        };
        extendedData = {
          offers: { ...baseData.offers, highestOffer: 15, offerList },
        };
        break;
      case 'pending-no-offers':
        baseData = {
          status: 'pending',
          thumbSaleState: 'pending',
        };
        extendedData = {
          offers: { ...baseData.offers },
        };
        break;
      case 'pending-some-offers':
        baseData = {
          status: 'pending',
          thumbSaleState: 'pending',
          offers: {
            numOffers: offerList.length,
            offerPrice: 10,
            offerEndTime,
          },
        };
        extendedData = {
          offers: { ...baseData.offers, highestOffer: 15, offerList },
        };
        break;
      case 'before-offer-window':
        baseData = {
          status: 'onsale',
          thumbSaleState: 'open',
          offers: {
            numOffers: 0,
            offerPrice: 10,
          },
        };
        extendedData = {
          offers: { ...baseData.offers },
        };
        break;
      default:
        break;
    }

    if (listingStatus.buyItNow) {
      baseData = {
        ...baseData,
        status: 'onsale',
        fixedPrice: {
          fixedPrice: 30,
        },
      };
      extendedData = {
        ...extendedData,
        fixedPrice: {
          fixedPrice: 30,
        },
      };
    }
  } else if (
    ['sold_via_buy_it_now', 'sold_via_offer'].includes(listingStatus)
  ) {
    baseData.status = 'sold';
    baseData.thumbSaleState = 'sold';

    if (listingStatus === 'sold_via_offer') {
      baseData.offers = {
        numOffers: 0,
        offerPrice: 10,
      };
      extendedData.lastTradeTs = oneDayAgoTs;
    } else {
      baseData.fixedPrice = {
        fixedPrice: 10,
      };
      extendedData.lastTradeTs = oneDayAgoTs;
    }
  } else if (listingStatus === 'not_onsale') {
    baseData.status = listingStatus;
  }

  return {
    isAdoptable: true,
    adoptable: {
      ...baseData,
    } as PapiDeviation['adoptable'],
    extended: {
      sellableAs:
        baseData.status === 'not_onsale'
          ? ['adoptable', 'pcp']
          : isResaleAllowed && baseData.status === 'sold'
          ? ['adoptable_resale']
          : [],
      adoptable: {
        resaleAllowed: isResaleAllowed,
        saleCount,
        deviationId: deviation.deviationId,
        creator: authorOverride ?? deviation.author,
        owner: chance.pickone(users),
        canAdopt: canCurrentUserBuy,
        isOwner:
          currentUserRelation === 'owner' ||
          currentUserRelation === 'creator-and-owner',
        isCreator: currentUserRelation === 'creator',
        creationTs: oneDayAgoTs,
        assetSourceUrl: deviation.url,
        isGiftable,
        wasGifted,
        earlyAccess,
        ...extendedData,
      },
    },
  };
}

// ----
export function extendDeviation(
  { dailyDeviation, pcp, ...deviation }: PapiDeviation,
  {
    withDailyDeviation = true,
    withDownload = true,
    withPcp = true,
    withCoreBadges = true,
    withStash = false,
    withPremiumGallery = false,
    withExclusiveGroup = false,
    withSaleDetails = false,
    withoutEligibleDiscount = false,
    adoptableConfig,
    isWatchersOnly = false,
    hasPGAccess = false,
    title,
    isOwner = false,
    isEyes = false,
    authorOverride,
    tier,
    isUserSubscribed = false,
    isLockedSubscribed = false,
    digitalAsset,
    withBackground = false,
    withMsPromo = false,
    withDreamup = false,
    withMuro = false,
    withBoostRestrictions = false,
    showMonetizationPromo,
    additionalMedia,
  }: {
    withDailyDeviation?: boolean;
    withDownload?: boolean;
    withPcp?: boolean;
    withCoreBadges?: boolean;
    withStash?: boolean;
    withPremiumGallery?: boolean;
    withExclusiveGroup?: boolean;
    adoptableConfig?: AdoptableConfig;
    isWatchersOnly?: boolean;
    hasPGAccess?: boolean;
    title?: string;
    isOwner?: boolean;
    isEyes?: boolean;
    authorOverride?: PapiUser;
    tier?: PapiDeviation;
    isUserSubscribed?: boolean;
    isLockedSubscribed?: boolean;
    digitalAsset?: any;
    withSaleDetails?: boolean;
    withoutEligibleDiscount?: boolean;
    withBackground?: boolean | PapiDeviationBackground;
    withMsPromo?: boolean;
    withDreamup?: boolean;
    withMuro?: boolean;
    withBoostRestrictions?: boolean;
    showMonetizationPromo?:
      | 'tier'
      | 'commission'
      | 'premium_gallery'
      | 'adoptable'
      | 'core';
    /** if true, will generate 3 to 35 additionalMedia
     *  if a number, will generate that many
     *  if an object, will use that as the extended deviation's `additionalMedia` */
    additionalMedia?:
      | boolean
      | number
      | PapiExtendedDeviation['additionalMedia'];
  } = {}
) {
  const extendedDeviation: PapiDeviation = {
    ...deviation,
    author: authorOverride ?? deviation.author,
    title: title ?? deviation.title,
    textContent: makeTextContent(deviation.textContent),
    extended: {
      sellableAs: ['adoptable', 'pcp'],
      isWatchingAuthor: !isOwner && bool(),
      canUserEdit: isOwner,
      canUserDelete: isOwner,
      canUserAddToGroup: true,
      editUrl: isOwner
        ? `https://www.deviantart.lan/deviation/edit/${deviation.deviationId}`
        : undefined,
      groupListUrl: `https://www.deviantart.lan/deviation/${deviation.deviationId}/groups`,
      descriptionText:
        deviation.extended?.descriptionText ||
        makeEditorTextWithOptions({
          legacy: true,
          excerpt:
            // note the double backslash: we're going to JSON.parse this later
            `(test descriptionText:) ${n(paragraph, 3).join('\\n')}`,
        }),
      originalFile: {
        type: 'png',
        height: 1000,
        width: 800,
        filesize: 12981512310,
      },
      subjectTags: [
        {
          name: 'potato',
          type: 'object',
          url: url({ domain: 'www.deviantart.lan' }),
        },
        {
          name: 'carrot',
          type: 'object',
          url: url({ domain: 'www.deviantart.lan' }),
        },
        {
          name: 'chris',
          type: 'model',
          url: url({ domain: 'www.deviantart.lan' }),
          user: users.find(u => u.userId === 3),
        },
        {
          name: 'Cookie Monster',
          type: 'character',
          url: url({ domain: 'www.deviantart.lan' }),
        },
      ],
      locationTag: {
        name: 'Cookie jar',
        url: url({ domain: 'www.deviantart.lan' }),
      },
      tags: n(word, 10).map(tag => ({
        name: tag,
        url: `https://www.deviantart.lan/tag/${tag}`,
      })),
      exif: [
        { tag: 'Make', value: 'EASTMAN KODAK COMPANY' },
        { tag: 'Model', value: 'KODAK EASYSHARE M340 Digital Camera' },
        { tag: 'Shutter', value: '1/10' },
        { tag: 'Aperture', value: 'F/3.1' },
        { tag: 'Focal Length', value: '6' },
        { tag: 'ISO', value: '80' },
        { tag: 'Date', value: new Date().toISOString() },
      ],
      stats: {
        views: 98231,
        today: 13,
        shares: 65,
        downloads: 45,
        groups: 3,
      },
      awardedBadges: withCoreBadges ? coreBadgesLimited : undefined,
      relatedContent: makeRelatedContent(
        deviation,
        tier,
        isUserSubscribed,
        withMsPromo,
        showMonetizationPromo
      ),
      exclusiveGroup: withExclusiveGroup ? groups[1] : undefined,
      showStageMonetizationPromo: showMonetizationPromo as any,
      ...(withMuro ? { muroImportUrl: chance.url() } : {}),
      ...(withBoostRestrictions
        ? {
            isBoostable: true,
            boosted: false,
            boostRestrictions: {
              canUseFreeBoost: true,
              canUsePaidBoost: true,
              freeBoostsAllowed: 3,
              freeBoostsRemaining: 2,
              boostPointsPrice: 250,
              pointsBalance: 1000,
            },
          }
        : {}),
    },
  };

  if (withDailyDeviation) {
    extendedDeviation.dailyDeviation =
      dailyDeviation ||
      (isEyes
        ? deviationsByType.daily[0].dailyDeviation
        : pickone(deviationsByType.daily).dailyDeviation);
  }

  if (withDownload) {
    extendedDeviation.isDownloadable = true;
    extendedDeviation.extended.download = {
      type: 'jpg',
      url: 'https://www.deviantart.lan/download/436648517/deviation_with_lots_of_comments_by_superbearpower-d77yw5h.png?token=f096ead0334c195ca094bce73698499368b140b9&ts=1524144117',
      width: 1366,
      height: 768,
      filesize: 273744,
    };
  } else {
    extendedDeviation.isDownloadable = false;
  }

  if (withSaleDetails) {
    extendedDeviation.extended.saleDetails = {
      subscription: {
        eligibleDiscount: withoutEligibleDiscount ? undefined : makeDiscount(),
        notEligibleDiscounts: [makeDiscount()],
      },
      adoptableFixed: {
        eligibleDiscount: withoutEligibleDiscount ? undefined : makeDiscount(),
        notEligibleDiscounts: [makeDiscount()],
      },
      pcp: {
        eligibleDiscount: withoutEligibleDiscount ? undefined : makeDiscount(),
        notEligibleDiscounts: [makeDiscount()],
      },
      premiumFolder: {
        eligibleDiscount: withoutEligibleDiscount ? undefined : makeDiscount(),
        notEligibleDiscounts: [makeDiscount()],
      },
    };
  }

  if (withPcp) {
    extendedDeviation.isPurchasable = true;
    extendedDeviation.extended.sellableAs = [];

    const downloadPrice = isEyes ? 1500 : integer({ min: 10, max: 9990 });
    const downloadDollarPrice = isEyes ? 18.75 : integer({ min: 1, max: 9999 });
    extendedDeviation.pcp = pcp || {
      downloadPrice,
      downloadDollarPrice,
    };

    extendedDeviation.extended.pcp = {
      downloadPrice,
      downloadDollarPrice,
      hasUserPurchased: false,
      zipFilesize: 80839123,
      url: 'https://www.deviantart.lan/pointsdownload/modal/display?deviationid=436649341',
      assets: [
        {
          type: 'jpg',
          filename: 'somefile.jpg',
          filesize: 1500,
          width: 300,
          height: 150,
        },
        {
          type: 'jpg',
          filename: 'some other file.jpg',
          filesize: 78000,
          width: 1980,
          height: 1297,
        },
        {
          type: 'jpg',
          filename: 'a file with a very very very very very long name.pdf',
          filesize: 693224,
          width: 800,
          height: 600,
        },
        {
          type: 'pdf',
          filename: 'big file.pdf',
          filesize: 693200024,
        },
      ],
    };
  }

  if (withBackground) {
    extendedDeviation.background =
      typeof withBackground === 'object'
        ? withBackground
        : getDeviationBackground();
  }

  if (tier) {
    extendedDeviation.tierAccess = isUserSubscribed ? 'unlocked' : 'locked';
    if (isLockedSubscribed) {
      isUserSubscribed = true;
      extendedDeviation.tierAccess = 'locked-subscribed';
    }

    extendedDeviation.primaryTier = {
      ...tier,
      tier: {
        ...tier.tier,
        isUserSubscribed,
      },
    };
  } else if (extendedDeviation.tier) {
    extendedDeviation.tier = {
      ...extendedDeviation.tier,
      isUserSubscribed,
    };
  }

  if (withPremiumGallery) {
    extendedDeviation.premiumFolderData = {
      type: 'paid',
      hasAccess: hasPGAccess,
      numSubscribers: 10,
      pointsPrice: 200,
      dollarPrice: 40.0,
      galleryId: 123123123123,
      galleryUrl: 'https://example.com',
    };

    if (isWatchersOnly) {
      extendedDeviation.premiumFolderData = {
        type: 'watchers',
        hasAccess: hasPGAccess,
        numSubscribers: 10,
      };
    }
  }

  if (withDreamup) {
    extendedDeviation.isDreamsofart = true;
    extendedDeviation.extended.showDreamupPromo = true;
    extendedDeviation.extended.dreamsofart = {
      prompt: paragraph(),
      parentDeviation: pickone(deviationsByType.image),
    };
  }

  if (withStash) {
    extendedDeviation.stashPrivateid = integer();
    extendedDeviation.isPublished = false;
    extendedDeviation.publishedTime = undefined;
  }

  if (adoptableConfig) {
    const adoptableData = makeAdoptableData(
      deviation,
      adoptableConfig,
      authorOverride
    );
    extendedDeviation.isAdoptable = adoptableData.isAdoptable;
    extendedDeviation.adoptable = adoptableData.adoptable;
    extendedDeviation.extended.sellableAs = adoptableData.extended.sellableAs;
    extendedDeviation.extended.adoptable = adoptableData.extended.adoptable;
  }

  if (additionalMedia) {
    extendedDeviation.isMultiImage = true;
    extendedDeviation.canBeMultiImage = true;
    if (typeof additionalMedia === 'object') {
      extendedDeviation.extended.additionalMedia = additionalMedia;
    } else if (typeof additionalMedia === 'number') {
      const numberToGenerate = additionalMedia;
      extendedDeviation.extended.additionalMedia =
        generateAdditionalMedia(numberToGenerate);
    } else if (typeof additionalMedia === 'boolean') {
      extendedDeviation.extended.additionalMedia = generateAdditionalMedia();
    }
  }

  return extendedDeviation;
}

export function updateDeviation(
  deviation: PapiDeviation,
  overrides: Partial<PapiDeviation>,
  strategy: 'replace' | 'extend' = 'replace'
) {
  if (strategy === 'replace') {
    return { ...deviation, ...overrides };
  } else {
    // strategy === 'extend'
    // We recursively merge the overrides with the existing deviation
    const merged = { ...deviation };
    const merge = (obj: any, overrideObj: any) => {
      for (const key in overrideObj) {
        if (isObject(overrideObj[key]) && isObject(obj[key])) {
          obj[key] = { ...obj[key] }; // create a shallow copy
          merge(obj[key], overrideObj[key]); // recursive $.merge(target, original);
        } else {
          obj[key] = overrideObj[key];
        }
      }
    };
    merge(merged, overrides);
    return merged;
  }
}
